import { Component, Input, OnInit, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FileVaultApiService } from '../../services/file-vault-api.service';
import { LifecycleState, LifecycleStateInfo } from '../../models/file-vault.models';

export interface LifecycleStateConfig {
  label: string;
  color: string;
  bgColor: string;
  icon: string;
}

@Component({
  selector: 'fv-lifecycle-badge',
  standalone: true,
  template: `
    <div class="lifecycle-badge"
         [class]="'state-' + stateKey"
         [style.backgroundColor]="config.bgColor"
         [style.color]="config.color"
         [style.borderColor]="config.color"
         [title]="tooltipText">
      <span class="state-icon" *ngIf="showIcon">{{ config.icon }}</span>
      <span class="state-label">{{ config.label }}</span>
      <span class="state-indicators" *ngIf="showIndicators">
        <span class="indicator" *ngIf="stateInfo?.isEditable" title="Editable">E</span>
        <span class="indicator released" *ngIf="stateInfo?.isReleased" title="Released">R</span>
        <span class="indicator final" *ngIf="stateInfo?.isFinal" title="Final">F</span>
      </span>
    </div>
  `,
  styles: [`
    .lifecycle-badge {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 4px 10px;
      border-radius: 4px;
      font-size: 12px;
      font-weight: 500;
      border: 1px solid;
      white-space: nowrap;
    }

    .state-icon {
      font-size: 14px;
    }

    .state-label {
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .state-indicators {
      display: flex;
      gap: 2px;
      margin-left: 4px;
    }

    .indicator {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      font-size: 9px;
      font-weight: bold;
      background: rgba(255,255,255,0.3);
    }

    .indicator.released {
      background: rgba(0,128,0,0.3);
    }

    .indicator.final {
      background: rgba(128,128,128,0.3);
    }

    /* State-specific styles */
    .state-inwork { }
    .state-inreview { }
    .state-approved { }
    .state-released { }
    .state-superseded { }
    .state-obsolete { }
  `],
  imports: [CommonModule]
})
export class LifecycleBadgeComponent implements OnInit, OnChanges {
  private api = inject(FileVaultApiService);

  /** Direct state value (alternative to entityType/entityId) */
  @Input() state?: LifecycleState;

  /** Entity type for API lookup */
  @Input() entityType?: string;

  /** Entity ID for API lookup */
  @Input() entityId?: string;

  /** Show state icon */
  @Input() showIcon = true;

  /** Show state indicators (Editable, Released, Final) */
  @Input() showIndicators = false;

  /** Size variant */
  @Input() size: 'small' | 'medium' | 'large' = 'medium';

  stateInfo?: LifecycleStateInfo;
  isLoading = false;

  private readonly stateConfigs: Record<number, LifecycleStateConfig> = {
    [LifecycleState.InWork]: {
      label: 'In Work',
      color: '#64b5f6',
      bgColor: '#1a2733',
      icon: '✏️'
    },
    [LifecycleState.InReview]: {
      label: 'In Review',
      color: '#ffb74d',
      bgColor: '#332b1a',
      icon: '👁️'
    },
    [LifecycleState.Approved]: {
      label: 'Approved',
      color: '#66bb6a',
      bgColor: '#1a2e1a',
      icon: '✓'
    },
    [LifecycleState.Released]: {
      label: 'Released',
      color: '#4caf50',
      bgColor: '#1a331a',
      icon: '🚀'
    },
    [LifecycleState.Superseded]: {
      label: 'Superseded',
      color: '#9e9e9e',
      bgColor: '#2d2d2d',
      icon: '↪️'
    },
    [LifecycleState.Obsolete]: {
      label: 'Obsolete',
      color: '#ef5350',
      bgColor: '#2e1a1a',
      icon: '⛔'
    }
  };

  private readonly defaultConfig: LifecycleStateConfig = {
    label: 'Unknown',
    color: '#9e9e9e',
    bgColor: '#2d2d2d',
    icon: '?'
  };

  get currentState(): LifecycleState {
    return this.state ?? this.stateInfo?.currentState ?? LifecycleState.InWork;
  }

  get config(): LifecycleStateConfig {
    return this.stateConfigs[this.currentState] ?? this.defaultConfig;
  }

  get stateKey(): string {
    return LifecycleState[this.currentState]?.toLowerCase() ?? 'unknown';
  }

  get tooltipText(): string {
    const base = this.config.label;
    const flags: string[] = [];

    if (this.stateInfo?.isEditable) flags.push('Editable');
    if (this.stateInfo?.isReleased) flags.push('Released');
    if (this.stateInfo?.isFinal) flags.push('Final State');

    return flags.length > 0 ? `${base} (${flags.join(', ')})` : base;
  }

  ngOnInit(): void {
    this.loadStateIfNeeded();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['entityType'] || changes['entityId']) && !changes['entityType']?.firstChange) {
      this.loadStateIfNeeded();
    }
  }

  private async loadStateIfNeeded(): Promise<void> {
    // Only load from API if entityType and entityId are provided and state is not directly set
    if (this.entityType && this.entityId && this.state === undefined) {
      this.isLoading = true;
      try {
        this.stateInfo = await this.api.getLifecycleState(this.entityType, this.entityId).toPromise();
      } catch (error) {
        console.error('Failed to load lifecycle state', error);
      } finally {
        this.isLoading = false;
      }
    }
  }

  /** Get state label for a given state value */
  static getStateLabel(state: LifecycleState): string {
    const labels: Record<number, string> = {
      [LifecycleState.InWork]: 'In Work',
      [LifecycleState.InReview]: 'In Review',
      [LifecycleState.Approved]: 'Approved',
      [LifecycleState.Released]: 'Released',
      [LifecycleState.Superseded]: 'Superseded',
      [LifecycleState.Obsolete]: 'Obsolete'
    };
    return labels[state] ?? 'Unknown';
  }
}
